import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearProductCreateJob,
  failProductCreateJob,
  getProductCreateJobStatus,
  requestManualProducts,
  setProductCreateJob,
} from "../actions/products";
import { setGlobalAlert } from "../actions/common";
import { uploadStagedFilesForJob } from "../helpers/productCreateStagedUploads";
import { publishingChipText, publishingNames } from "../helpers/channelPublish";
import { updateInsertRule } from "../api/inserts";
import "../pages/listings/styles.scss";

export const PRODUCT_CREATE_JOB_KEY = "riverr.productCreateJob";
const brandingMetaKey = (jobId) => `riverr.brandingCreateJob.${jobId}`;
const POLL_MS = 2500;
const UPLOAD_STATUSES = new Set(["awaiting_uploads", "uploading"]);
const ACTIVE = new Set([
  "awaiting_uploads",
  "uploading",
  "queued",
  "validating",
  "creating",
  "processing",
]);

function readBrandingMeta(jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(brandingMetaKey(jobId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function clearBrandingMeta(jobId) {
  if (!jobId) return;
  try {
    sessionStorage.removeItem(brandingMetaKey(jobId));
  } catch (_) {
    /* ignore */
  }
}

/**
 * App-level poller for durable product create/update jobs.
 * Survives navigation so users can leave the designer while save finishes.
 * Also resumes signed-URL uploads from IndexedDB when status is awaiting_uploads.
 */
const ProductCreateJobWatcher = () => {
  const dispatch = useDispatch();
  const job = useSelector((state) => state?.products?.productCreateJob);
  const pollTimerRef = useRef(null);
  const finishingRef = useRef(false);
  const uploadStartedRef = useRef(new Set());
  const [floatHidden, setFloatHidden] = useState(false);

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearStoredJob = () => {
    try {
      localStorage.removeItem(PRODUCT_CREATE_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const refreshProducts = () => {
    dispatch(requestManualProducts({})).catch(() => {});
  };

  const isQuietJob = (status) =>
    Boolean(
      status?.quiet ||
        status?.source === "storeBranding" ||
        status?.result?.hideFromMyProducts ||
        job?.quiet ||
        job?.source === "storeBranding" ||
        readBrandingMeta(status?.jobId || job?.jobId)?.quiet
    );

  const finishJob = (status) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearPoll();
    clearStoredJob();
    dispatch(setProductCreateJob(status));
    refreshProducts();

    const quiet = isQuietJob(status);
    const branding = readBrandingMeta(status?.jobId || job?.jobId);
    const ruleId = branding?.ruleId || job?.brandingRuleId || null;
    const finalProductId =
      status?.productId || status?.result?.id || job?.productId || null;

    if (status?.status === "succeeded" && ruleId && finalProductId) {
      // Prefer final product id (draft may have been replaced) + first image.
      const imageUrl =
        status?.result?.previewUrl ||
        status?.result?.imageUrl ||
        (Array.isArray(status?.result?.images)
          ? status.result.images.find((i) => i?.url)?.url
          : null) ||
        null;
      updateInsertRule(ruleId, {
        designProductId: String(finalProductId),
        ...(typeof imageUrl === "string" && /^https?:\/\//i.test(imageUrl)
          ? { designImageUrl: String(imageUrl) }
          : {}),
        ...(branding?.designSlotId
          ? { designSlotId: String(branding.designSlotId) }
          : {}),
        ...(branding?.makeActive === true ? { makeActive: true } : {}),
      }).catch((err) => {
        console.warn("[store-branding] watcher rule update failed", err);
      });
    }

    if (status?.status === "succeeded") {
      if (!quiet) {
        const shopWarning = status?.result?.shopSubmitError;
        const names = publishingNames(status?.result?.channels);
        const publishing =
          names.length > 0 || status?.result?.channelPublish === "queued";
        const publishFailed = status?.result?.channelPublish === "failed";
        const channelName = names[0] || "your sales channel";
        // A queued publish stays on the chip. The toast fires when the
        // product document flips to live.
        if (!publishing || shopWarning || publishFailed) {
          dispatch(
            setGlobalAlert({
              type: shopWarning || publishFailed ? "warning" : "success",
              message: shopWarning
                ? `"${status.title || "Product"}" saved to My Products, but could not publish to your shop: ${shopWarning}`
                : publishFailed
                  ? `Product saved. Publishing to ${channelName} could not be queued. Retry from My Products.`
                  : status?.mode === "update"
                    ? `"${status.title || "Product"}" updated successfully.`
                    : `"${status.title || "Product"}" is ready in My Products.`,
            })
          );
        }
      }
    } else if (!quiet) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message:
            status?.error ||
            "Product create failed. Please open the designer and try again.",
        })
      );
    }

    clearBrandingMeta(status?.jobId || job?.jobId);

    setTimeout(() => {
      dispatch(clearProductCreateJob());
      finishingRef.current = false;
      refreshProducts();
    }, quiet ? 500 : 6000);
  };

  const markUploadFailed = async (jobId, message) => {
    try {
      await failProductCreateJob(jobId, { error: message });
    } catch (_) {
      /* best-effort — local UI still shows failure */
    }
    refreshProducts();
  };

  const resumeUploadsIfNeeded = async (jobId, status) => {
    if (!UPLOAD_STATUSES.has(status)) return;
    if (uploadStartedRef.current.has(jobId)) return;
    uploadStartedRef.current.add(jobId);
    try {
      await uploadStagedFilesForJob(jobId, (progress) => {
        dispatch(
          setProductCreateJob({
            jobId,
            status: progress.status || "uploading",
            stage:
              progress.uploadProgress != null
                ? `Uploading designs (${progress.uploadProgress.completed}/${progress.uploadProgress.total})…`
                : `Uploading designs (${progress.completed}/${progress.total})…`,
            uploadProgress: progress.uploadProgress || {
              completed: progress.completed,
              total: progress.total,
              percent: progress.percent,
            },
            draftProductId: job?.draftProductId,
            productId: job?.productId || job?.draftProductId,
            title: job?.title,
            mode: job?.mode,
            quiet: job?.quiet,
            source: job?.source,
            brandingRuleId: job?.brandingRuleId,
          })
        );
      });
    } catch (err) {
      uploadStartedRef.current.delete(jobId);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Design upload could not resume. Re-open the designer and save again.";
      // Transient empty-IDB / network — keep polling a bit before failing hard.
      if (/no longer available|failed to fetch|network error/i.test(message)) {
        throw err;
      }
      clearPoll();
      clearStoredJob();
      await markUploadFailed(jobId, message);
      dispatch(
        setProductCreateJob({
          jobId,
          status: "failed",
          error: message,
          quiet: job?.quiet,
          source: job?.source,
        })
      );
      if (!isQuietJob(job)) {
        dispatch(setGlobalAlert({ type: "error", message }));
      }
      setTimeout(() => {
        dispatch(clearProductCreateJob());
      }, 6000);
      throw err;
    }
  };

  const pollJob = async (jobId, attempt = 0) => {
    try {
      const res = await getProductCreateJobStatus(jobId);
      const status = res?.data;
      if (!status) throw new Error("Unable to read create progress.");

      dispatch(
        setProductCreateJob({
          ...status,
          quiet: job?.quiet || readBrandingMeta(jobId)?.quiet,
          source: job?.source || (readBrandingMeta(jobId)?.quiet ? "storeBranding" : undefined),
          brandingRuleId:
            job?.brandingRuleId || readBrandingMeta(jobId)?.ruleId || null,
        })
      );

      if (UPLOAD_STATUSES.has(status.status)) {
        try {
          await resumeUploadsIfNeeded(jobId, status.status);
        } catch (_) {
          // Retry — IndexedDB may still be writing, or a single upload blipped.
          if (attempt < 12) {
            pollTimerRef.current = setTimeout(
              () => pollJob(jobId, attempt + 1),
              POLL_MS
            );
            return;
          }
          const message =
            "Design upload stalled. Re-open the designer and save again.";
          await markUploadFailed(jobId, message);
          finishJob({
            jobId,
            status: "failed",
            error: message,
            title: job?.title,
            quiet: job?.quiet,
            source: job?.source,
          });
          return;
        }
        pollTimerRef.current = setTimeout(() => pollJob(jobId, 0), POLL_MS);
        return;
      }

      if (ACTIVE.has(status.status)) {
        pollTimerRef.current = setTimeout(() => pollJob(jobId, 0), POLL_MS);
        return;
      }

      finishJob({
        ...status,
        quiet: job?.quiet || readBrandingMeta(jobId)?.quiet,
        source: job?.source,
        brandingRuleId:
          job?.brandingRuleId || readBrandingMeta(jobId)?.ruleId || null,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to check product create progress.";
      // Don't kill a healthy job on a transient poll/network blip.
      if (
        attempt < 8 &&
        (!err?.response || /failed to fetch|network error/i.test(message))
      ) {
        pollTimerRef.current = setTimeout(
          () => pollJob(jobId, attempt + 1),
          POLL_MS
        );
        return;
      }
      clearPoll();
      clearStoredJob();
      await markUploadFailed(jobId, message);
      dispatch(
        setProductCreateJob({
          jobId,
          status: "failed",
          error: message,
          quiet: job?.quiet,
          source: job?.source,
        })
      );
      if (!isQuietJob(job)) {
        dispatch(setGlobalAlert({ type: "error", message }));
      }
      setTimeout(() => {
        dispatch(clearProductCreateJob());
      }, 6000);
    }
  };

  useEffect(() => {
    let jobId = null;
    try {
      jobId = localStorage.getItem(PRODUCT_CREATE_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
    if (jobId) {
      const branding = readBrandingMeta(jobId);
      dispatch(
        setProductCreateJob({
          jobId,
          status: "awaiting_uploads",
          stage: "Resuming product create…",
          ...(branding?.quiet
            ? {
                quiet: true,
                source: "storeBranding",
                brandingRuleId: branding.ruleId || null,
              }
            : {}),
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const jobId = job?.jobId;
    if (!jobId || !ACTIVE.has(job?.status)) {
      return clearPoll;
    }
    setFloatHidden(false);
    clearPoll();
    pollJob(jobId);
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.jobId, job?.status]);

  // Branding create jobs finish silently — no My Products float.
  if (!job || floatHidden || isQuietJob(job)) return null;

  const isActive = ACTIVE.has(job.status);
  const isFail = job.status === "failed";
  const isOk = job.status === "succeeded";
  if (!isActive && !isFail && !isOk) return null;

  const isUploading = UPLOAD_STATUSES.has(job.status);
  const pendingNames = publishingNames(job?.result?.channels);
  const publishing =
    isOk && (pendingNames.length > 0 || job?.result?.channelPublish === "queued");
  const publishFailed = isOk && job?.result?.channelPublish === "failed";
  const channelLabel = pendingNames.filter(Boolean).join(" and ") || "your sales channel";
  const label = isFail
    ? "Failed to create product"
    : publishFailed || publishing
      ? "Product saved"
      : isOk
        ? "Product ready"
        : isUploading
          ? "Uploading designs…"
          : "Creating product…";

  const progressPct = (() => {
    if (job.uploadProgress?.percent != null) return job.uploadProgress.percent;
    if (job.status === "queued") return 20;
    if (job.status === "validating") return 40;
    if (job.status === "creating") return 70;
    if (isUploading) return Math.max(5, job.uploadProgress?.percent || 10);
    return 50;
  })();

  return (
    <div
      className="listings-bulk-ignore-float"
      role="status"
      aria-live="polite"
      style={
        isFail
          ? { borderColor: "#f5c2c2" }
          : isOk
            ? { borderColor: "#b7e4c7" }
            : undefined
      }
    >
      <strong>{label}</strong>
      <div>
        {job.title ? <div style={{ marginBottom: 4 }}>{job.title}</div> : null}
        {isActive &&
          (job.stage || "Working in the background. Check My Products.")}
        {isFail && (job.error || "Something went wrong.")}
        {publishing && (
          <span>
            <span className="listings-bulk-ignore-float__spin" aria-hidden />
            {publishingChipText(channelLabel)}
          </span>
        )}
        {publishFailed &&
          "Publishing could not be queued. Retry from My Products."}
        {isOk && !publishing && !publishFailed && "Saved. It’s in My Products."}
      </div>
      {isActive && (
        <div className="listings-bulk-ignore-float__track">
          <div
            className="listings-bulk-ignore-float__fill"
            style={{
              width: `${Math.min(95, Math.max(5, progressPct))}%`,
              animation: "none",
            }}
          />
        </div>
      )}
      <button
        type="button"
        className="listings-bulk-ignore-float__dismiss"
        onClick={() => setFloatHidden(true)}
      >
        Hide
      </button>
    </div>
  );
};

export default ProductCreateJobWatcher;
